import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';

import { CommonModule } from '@angular/common';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatButtonToggle,
  MatButtonToggleChange,
  MatButtonToggleGroup,
} from '@angular/material/button-toggle';
import {
  MatNativeDateModule,
  provideNativeDateAdapter,
} from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { RouterModule } from '@angular/router';
import { DatetimeRangePickerComponent } from '@components/shared/datetime-range-picker/datetime-range-picker.component';
import { ALERT_SUMMARY_BUTTON_OPTIONS } from './alert-summary.constant';

/** 計測種別 */
export type SensingType =
  | 'temperature'
  | 'humidity'
  | 'pressure'
  | 'light'
  | 'tilt'
  | 'fallDetected'
  | 'mcdDetected'
  | 'lightExposure'
  | 'shockDetected';

/** サマリーボタン */
export type summaryBtnOption = {
  type: 'sensor' | 'event';
  label: string;
  iconName: string;
  unit: string;
  key: keyof SensingListViewModel;
};

/** 一つのセンサの計測結果のViewModel */
export type SensingViewModel = {
  status?: string;
  value?: number;
  eventAt?: string;
};

/** 複数センサの計測結果のViewModel */
export type SensingListViewModel = {
  /** 温度 */
  temperature?: SensingViewModel;
  /** 気圧 */
  pressure?: SensingViewModel;
  /** 湿度 */
  humidity?: SensingViewModel;
  /** 傾斜 */
  tilt?: SensingViewModel;
  /** 照度 */
  light?: SensingViewModel;
  /** 落下検知 */
  fallDetected?: SensingViewModel;
  /** 動作検知 */
  mcdDetected?: SensingViewModel;
  /** 明暗検知 */
  lightExposure?: SensingViewModel;
  /** 衝撃検知 */
  shockDetected?: SensingViewModel;
};

/** アラート情報 */
export type Alert = {
  device_id: string;
  device_name: string;
  device_info_sensing: SensingListViewModel[];
};

/** ユーザー種別 */
export type PageViewModel = 'device' | 'shipment';

@Component({
  selector: 'app-alert-summary',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormField,
    MatLabel,
    MatDatepickerModule,
    MatInputModule,
    MatNativeDateModule,
    MatButtonToggle,
    MatButtonToggleGroup,
    DatetimeRangePickerComponent,
  ],
  templateUrl: './alert-summary.component.html',
  styleUrl: './alert-summary.component.scss',
  providers: [provideNativeDateAdapter()],
})
export class AlertSummaryComponent implements OnChanges {
  /** デバイス最新情報 */
  @Input({ required: true }) public latestDevicesSummary: SensingListViewModel =
    {};

  /** デバイスタブの検索フォーム */
  @Input() public deviceHistoryFormGroup: FormGroup | undefined;

  /** 配送タブの検索フォーム */
  @Input() public shipmentHistoryFormGroup: FormGroup | undefined;

  /** 非活性状態か */
  @Input() public disabled = false;

  /** テナント作成日時 */
  @Input() public tenantCreatedAt: string | null = '';

  /** 削除を通知 */
  @Output() historyFiltered = new EventEmitter<void>();

  /** 計測選択を通知 */
  @Output() sensingTypeChanged = new EventEmitter<SensingType>();

  /** 表示ページ選択を通知 */
  @Output() displayPageChanged = new EventEmitter<PageViewModel>();

  /** ボタン種別 */
  public selectedBtn: SensingType | '' = '';

  /** 選択された表示画面 */
  public selectedDisplayPage: PageViewModel = 'device';

  /** サマリー表示のボタン設定 */
  public readonly summaryBtnOptions: summaryBtnOption[] =
    ALERT_SUMMARY_BUTTON_OPTIONS;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['latestDevicesSummary'] !== undefined) {
      // 設定が有、ONのセンサーで一番左にあるボタンをアクティブにする
      this.selectedBtn = '';
      for (const btnOption of this.summaryBtnOptions) {
        const key = btnOption.key;
        if (this.isExist(key, btnOption.type)) {
          this.selectedBtn = key;
          this.sensingTypeChanged.emit(key);
          break;
        }
      }
    }
  }

  /**
   * サマリーにあるセンサーの選択が変更された際に親に通知する
   * @param event 選択の変更イベント
   */
  public onSensingBtnChanged(event: MatButtonToggleChange) {
    this.sensingTypeChanged.emit(event.value);
  }

  /**
   * センサーのデータが存在するか
   * @param key キー
   * @param type タイプ
   * @returns trueならデータが存在する
   */
  public isExist(
    key: keyof SensingListViewModel,
    type: 'sensor' | 'event',
  ): boolean {
    if (!this.latestDevicesSummary) {
      return false;
    }
    if (type === 'sensor') {
      const value = this.latestDevicesSummary[key]?.value;
      return value != null;
    } else {
      const eventAt = this.latestDevicesSummary[key]?.eventAt;
      return eventAt != null;
    }
  }

  /**
   * センサーの値を取得する
   * @param key キー
   * @returns センサーの値
   */
  public getSensingValue(key: keyof SensingListViewModel): string {
    const value = this.latestDevicesSummary[key]?.value;
    return value ? `${value}` : '';
  }

  /**
   * センサーのステータスを取得する
   * @param key キー
   * @param type タイプ
   * @returns ステータス
   */
  public getSensingStatus(
    key: keyof SensingListViewModel,
    type: 'sensor' | 'event',
  ): string {
    if (type === 'sensor') {
      const status = this.latestDevicesSummary[key]?.status;
      return status || 'unknown';
    } else {
      const eventAt = this.latestDevicesSummary[key]?.eventAt;
      return eventAt ? 'Alert' : 'normal';
    }
  }

  /**
   * センサーのイベント日付を取得する
   * @param key キー
   * @returns 日付
   */
  public getSensingDate(key: keyof SensingListViewModel): string {
    const eventAt = this.latestDevicesSummary[key]?.eventAt;
    const eventAtList = eventAt?.split('T');
    return eventAtList && eventAtList.length > 0 ? eventAtList[0] : '';
  }

  /**
   * センサーのイベント日時を取得する
   * @param key キー
   * @returns 日時
   */
  public getSensingDateTime(key: keyof SensingListViewModel): string {
    return this.latestDevicesSummary[key]?.eventAt || '';
  }
}
